/**
 * @file CoreServiceFactory.ts
 * @role Constructs the application's infrastructure services as one dependency graph.
 *       Bootstrapper remains the composition root: it selects the skin/environment,
 *       invokes this factory, owns lifecycle sequencing, and registers the result.
 * @owns Service construction only.
 * @reads The selected presentation skin, host element, and canonical game environment.
 * @neverDoes Initialize services. Start listeners. Load assets. Register services.
 * @invariants 13 (all runtime dependencies originate from the composition root).
 * @see src/app/Bootstrapper.ts, docs/ARCHITECTURE.md
 */
import { GameApplication } from './GameApplication';
import type { GameEnvironment } from './GameEnvironment';
import { GameTicker } from './GameTicker';
import type { GameSkin } from './GameSkin';
import { AnalyticsService, createLoggerAnalyticsSink } from '@core/analytics/AnalyticsService';
import { AssetService } from '@core/assets/AssetService';
import type { TextureResolver } from '@core/assets/TextureResolver';
import { AudioService } from '@core/audio/AudioService';
import { HowlerAudioBackend } from '@core/audio/HowlerAudioBackend';
import { ConfigService } from '@core/config/ConfigService';
import type { UiConfig } from '@core/config/UiConfig';
import { DiagnosticsService } from '@core/diagnostics/DiagnosticsService';
import { EventBus } from '@core/events/EventBus';
import { GraphicsQualityService } from '@core/graphics/GraphicsQualityService';
import { LayoutService } from '@core/layout/LayoutService';
import { readSafeAreaInsets } from '@core/layout/SafeArea';
import { ConsoleLogger, noopLogger, type Logger } from '@core/logging/Logger';
import { ParticleService } from '@core/particles/ParticleService';
import { PostFxService } from '@core/postfx/PostFxService';
import { SettingsService } from '@core/settings/SettingsService';
import { GameClock } from '@core/time/GameClock';
import { AmbientFxService } from '@game/ambient/AmbientFxService';
import type { Texture } from 'pixi.js';

export interface CoreServices {
  environment: GameEnvironment;
  logger: Logger;
  eventBus: EventBus;
  app: GameApplication;
  ticker: GameTicker;
  clock: GameClock;
  layout: LayoutService;
  assets: AssetService;
  config: ConfigService;
  uiConfig: UiConfig;
  settings: SettingsService;
  graphicsQuality: GraphicsQualityService;
  analytics: AnalyticsService;
  audio: AudioService;
  particles: ParticleService;
  postFx: PostFxService;
  ambientFx: AmbientFxService;
  resolveTexture: TextureResolver;
  diagnostics: DiagnosticsService;
  skin: GameSkin;
}

export function createCoreServices(
  host: HTMLElement,
  skin: GameSkin,
  environment: GameEnvironment,
): CoreServices {
  const logger = environment.isDev ? new ConsoleLogger() : noopLogger;
  const eventBus = new EventBus();
  const assets = new AssetService(eventBus);
  const resolveTexture: TextureResolver = (assetId) => assets.getOptional<Texture>(assetId);
  if (environment.isDev) {
    for (const issue of skin.audioSourceIssues) logger.warn(`[audio] ${issue}`);
  }

  return {
    environment,
    logger,
    eventBus,
    app: new GameApplication(),
    ticker: new GameTicker(),
    clock: new GameClock(),
    layout: new LayoutService(eventBus, {
      safeAreaProvider: () => readSafeAreaInsets(host),
    }),
    assets,
    resolveTexture,
    config: new ConfigService(),
    uiConfig: skin.ui,
    settings: new SettingsService(undefined, undefined, logger),
    graphicsQuality: new GraphicsQualityService(),
    analytics: new AnalyticsService(createLoggerAnalyticsSink(logger)),
    audio: new AudioService(new HowlerAudioBackend(), skin.audio, skin.audioSources, logger),
    particles: new ParticleService(skin.particles),
    postFx: new PostFxService(skin.fx),
    ambientFx: new AmbientFxService(skin.ambient, resolveTexture, eventBus),
    diagnostics: new DiagnosticsService(undefined, logger),
    skin,
  };
}
